import React, { useState } from 'react'
import styled from 'styled-components'
import { TextField, Button, Box, Typography, FormControlLabel, Checkbox, Grid } from '@material-ui/core';
import { nanoid } from 'nanoid';

import { SingleAttributeTemplate, MultiAttributeTemplate, TableAttributeTemplate, ImageAttributeTemplate, NondispleyAttributeTemplate, CardTemplateAttributeDb } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateTextShape, CardTemplateCircleShape, CardTemplateRectangleShape, CardTemplateBalloonShape, CardTemplateImageShape, CardTemplateShapeDb } from '../redux/cardTemplateShapeDbSlice';
import { SingleAttribute, MultiAttribute, TableAttribute, ImageAttribute, NondispleyAttribute, CardAttributeDb } from '../redux/cardAttributeDbSlice';
import { CardTextShape, CardCircleShape, CardRectangleShape, CardBalloonShape, CardImageShape, CardShapeDb } from '../redux/cardShapeDbSlice';
import { FontType, ImageData } from '../redux/cardAttributeModelIndex';
import { deepCopy } from '../general';
import EditItemFont from '../components/EditItemFont'
import ItemNameInput from '../components/ItemNameInput';
import EditItemPosition from '../components/EditItemPosition';
import EditItemFrame from './EditItemFrame';
import EditShape from '../components/EditShape';
import TrimmingDialog from './TrimmingDialog';
import ColorPalette from './ColorPalette';

const ItemField = styled(TextField)`
  width: 99%;
`
const LabelTypography = styled(Typography)`
  color: #757575;
`

const isAttribute = (obj: any): obj is CardTemplateAttributeDb | CardAttributeDb => {
  return typeof obj?.cardAttributeModel === 'string';
}
const isShape = (obj: any): obj is CardTemplateShapeDb | CardShapeDb => {
  return typeof obj?.kind === 'string';
}
const isTemplate = (obj: any): obj is CardTemplateAttributeDb | CardTemplateShapeDb => {
  return typeof obj?.cardTemplateId === 'string';
}
const isSingle = (obj: CardTemplateAttributeDb | CardAttributeDb): obj is SingleAttributeTemplate | SingleAttribute => {
  return obj.cardAttributeModel === 'single';
}
const isMulti = (obj: CardTemplateAttributeDb | CardAttributeDb): obj is MultiAttributeTemplate | MultiAttribute => {
  return obj.cardAttributeModel === 'multi';
}
const isImage = (obj: any): obj is ImageAttributeTemplate | ImageAttribute | CardTemplateImageShape => {
  return obj?.cardAttributeModel === 'image' || obj?.kind === 'image';
}

type TextAttribute = SingleAttributeTemplate | MultiAttributeTemplate | SingleAttribute | MultiAttribute
//テキスト型項目　詳細タブ
type TextAttributeDetailProps = {
  id?: string;
  item: TextAttribute;
  changeItem: (item: any) => void;
  changeBackgroundDisplay: (display: boolean) => void;
  changeBackgroundColor: (color: string) => void;
  font: FontType;
  changeDescriptionDisplay: (display: boolean) => void;
}
const TextAttributeDetail: React.FC<TextAttributeDetailProps> = ({id, item, changeItem, changeBackgroundDisplay, changeBackgroundColor, font, changeDescriptionDisplay}) => {
  const attributeModel = isSingle(item) ? 'single' : 'multi';

  const changeHint = (newValue: string) => {
    const newItem = deepCopy(item);
    newItem.hint = newValue;
    changeItem(newItem);
  }
  const changeDefaultContent = (newValue: string) => {
    const newItem = deepCopy(item);
    newItem.defaultContent = newValue;
    changeItem(newItem);
  }
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(item);
    newItem.font = newFont;
    changeItem(newItem);
  }
  const changeIsMarkdown = (newValue: boolean) => {
    if (!isMulti(item)) return;
    const newItem = deepCopy(item);
    newItem.isMarkdown = newValue;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={item} changeItem={changeItem} />
      <Box mt={3}>
        <EditItemFrame id={id} item={item} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <Grid container alignItems="center">
          <FormControlLabel label="背景表示"
            control={
              <Checkbox checked={item.background.display} color="primary"
                onChange={(ev) => changeBackgroundDisplay(ev.currentTarget.checked)} />
            } />
          <ColorPalette
            currentValue={item.background.color}
            onChange={changeBackgroundColor}
            disabled={!item.background.display}
          />
        </Grid>
      </Box>
      { isMulti(item) &&
        <Box mt={3}>
          <FormControlLabel label="マークダウン"
            control={
              <Checkbox checked={item.isMarkdown} color="primary"
                onChange={(ev) => changeIsMarkdown(ev.currentTarget.checked)} />
            } />
        </Box>
      }
      <Box mt={3}>
        <ItemField variant='outlined' 
          label='ヒント'
          value={item.hint}
          onChange={(ev) => changeHint(ev.target.value)}
        />
      </Box>
      <Box mt={3}>
        { attributeModel === 'single' ?
          <ItemField variant='outlined'
            label='初期値'
            value={item.defaultContent}
            onChange={(ev) => changeDefaultContent(ev.target.value)}
          />
        :
          <ItemField variant='outlined'
            multiline minRows={8} maxRows={8}
            label='初期値'
            value={item.defaultContent}
            onChange={(ev) => changeDefaultContent(ev.target.value)}
          />
        }
      </Box>
      <Box mt={3}>
        <EditItemFont kind={attributeModel} font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={item} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <ItemDescription item={item} changeItem={changeItem}
          changeDescriptionDisplay={changeDescriptionDisplay} />
      </Box>
    </Box>
  );
}

//表型項目　詳細タブ
type TableAttributeDetailType = {
  id: string;
  item: TableAttributeTemplate | TableAttribute;
  changeItem: any;
  font: FontType;
  changeDescriptionDisplay:  (display: boolean) => void;
}
const TableAttributeDetail: React.FC<TableAttributeDetailType> = ({id, item, changeItem, font, changeDescriptionDisplay}) => {
  const columnIds = Object.keys(item.columnNames);
  const columnNames = columnIds.map(id => item.columnNames[id]);
  const [newColumnValue, setNewColumnValue] = useState('');
  type TargetType = { id: string; value: TableAttributeTemplate | TableAttribute; }
  const [target, setTarget] = useState<TargetType | undefined>(undefined);
  const [composing, setComposing] = useState(false);

  const changeColumnNames = (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, ix: number) => {
    const value = ev.currentTarget.value;
    const newItem = deepCopy(item);
    newItem.columnNames[columnIds[ix]] = value;
    changeItem(newItem);
    setTarget(undefined);
  }
  const deleteColumn = (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, ix: number) => {
    const value = ev.currentTarget.value;
    const newItem = deepCopy(item);
    if (value === '') delete newItem.columnNames[columnIds[ix]];
    changeItem(newItem);
    setTarget(undefined);
  }
  const changeNewColumn = (value: string) => {
    setTarget({id: id, value: item});
    setNewColumnValue(value);
  }
  const addColumn = () => {
    if (newColumnValue === '' || target === undefined) return;
    const newItem = deepCopy(target.value);
    const newId = nanoid();
    newItem.columnNames[newId] = newColumnValue;
    changeItem(newItem, target.id);
    setNewColumnValue('');
    setTarget(undefined);
  }
  const fontUpdate = (newFont: FontType) => {
    const newItem = deepCopy(item);
    newItem.font = newFont;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={item} changeItem={changeItem} />
      <LabelTypography variant="caption">表型の列名</LabelTypography>
      <Box>
        { columnNames.map((columnName, ix) =>
          <TextField margin="dense" variant="outlined" value={columnName}
            style={{width: '70%'}}
            onChange={(ev) => changeColumnNames(ev, ix)}
            onBlur={(ev) => deleteColumn(ev, ix)} />
        ) }
        <TextField margin="dense" variant="outlined" placeholder="列を追加します"
          value={newColumnValue} style={{width: '70%'}}
          onFocus={() => setTarget({id: id, value: item})}
          onChange={(ev) => changeNewColumn(ev.currentTarget.value)}
          onBlur={() => addColumn()}
          onCompositionStart={() => setComposing(true)}
          onCompositionEnd={() => setComposing(false)}
          onKeyDown={(ev) => ev.keyCode === 13 && !composing ? addColumn() : undefined} />
      </Box>
      <Box mt={3}>
        <EditItemFont kind='table' font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={item} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <ItemDescription item={item} changeItem={changeItem}
          changeDescriptionDisplay={changeDescriptionDisplay} />
      </Box>
    </Box>
  );
}

//画像型項目　詳細タブ
type ImageAttributeDetailProps = {
  id?: string;
  item: CardTemplateAttributeDb | CardAttributeDb;
  changeItem: any;
  changeBackgroundDisplay: (display: boolean) => void;
  changeBackgroundColor: (color: string) => void;
  onTrimming: () => void;
  changeDescriptionDisplay: (display: boolean) => void;
}
const ImageAttributeDetail: React.FC<ImageAttributeDetailProps> = ({id, item, changeItem, changeBackgroundDisplay, changeBackgroundColor, onTrimming, changeDescriptionDisplay}) => {
  const changeHint = (newValue: string) => {
    const newItem = deepCopy(item);
    newItem.hint = newValue;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={item} changeItem={changeItem} />
      <Box mt={3}>
        <EditItemFrame id={id} item={item} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <Grid container alignItems="center">
          <FormControlLabel label="背景表示"
            control={
              <Checkbox checked={item.background.display} color="primary"
                onChange={(ev) => changeBackgroundDisplay(ev.currentTarget.checked)} />
            } />
          <ColorPalette
            currentValue={item.background.color}
            onChange={changeBackgroundColor}
            disabled={!item.background.display}
          />
        </Grid>
      </Box>
      <Box mt={2}>
        <ItemField variant='outlined' label='ヒント'
          value={item.hint}
          onChange={(ev) => changeHint(ev.target.value)}
        />
      </Box>
      <Box mt={2}>
        <Button variant="outlined" color="primary" onClick={onTrimming}>トリミングする</Button>
      </Box>
      <Box mt={3}>
        <EditItemPosition item={item} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <ItemDescription item={item} changeItem={changeItem}
          changeDescriptionDisplay={changeDescriptionDisplay} />
      </Box>
    </Box>
  );
}
//非表示型項目　詳細タブ
type NondisplayAttributeDetailProps = {
  item: NondispleyAttributeTemplate | NondispleyAttribute;
  changeItem: (item: any) => void;
  changeDescriptionDisplay:  (display: boolean) => void;
}
const NondisplayAttributeDetail: React.FC<NondisplayAttributeDetailProps> = ({item, changeItem, changeDescriptionDisplay}) => {
  const changeHint = (newValue: string) => {
    const newItem = deepCopy(item);
    newItem.hint = newValue;
    changeItem(newItem);
  }
  const changeDefaultContent = (newValue: string) => {
    const newItem = deepCopy(item);
    newItem.defaultContent = newValue;
    changeItem(newItem);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={item} changeItem={changeItem} />
      <Box mt={3}>
        <ItemField variant='outlined' 
          label='ヒント'
          value={item.hint}
          onChange={(ev) => changeHint(ev.target.value)}
        />
      </Box>
      <Box mt={3}>
        <ItemField variant='outlined'
          multiline minRows={2} maxRows={8}
          label='初期値'
          value={item.defaultContent}
          onChange={(ev) => changeDefaultContent(ev.target.value)}
        />
      </Box>
      <Box mt={3}>
        <ItemDescription item={item} changeItem={changeItem}
          changeDescriptionDisplay={changeDescriptionDisplay} />
      </Box>
    </Box>
  );
}
//アイテム説明　詳細タブ
type ItemDescriptionProps = {
  item: SingleAttributeTemplate | MultiAttributeTemplate | SingleAttribute | MultiAttribute | TableAttributeTemplate | TableAttribute | CardTemplateAttributeDb | CardAttributeDb;
  changeItem: (item: any) => void;
  changeDescriptionDisplay: (display: boolean) => void;
}
const ItemDescription: React.FC<ItemDescriptionProps> = ({item, changeItem, changeDescriptionDisplay}) => {
  const changeDescription = (newValue: string) => {
    const newItem = deepCopy(item);
    newItem.description.content = newValue;
    changeItem(newItem);
  }

  return (
    <Box mt={4}>
      <Box mt={3}>
        <FormControlLabel label="アイテム説明表示"
          control={
            <Checkbox checked={item.description.display} color="primary"
              onChange={(ev) => changeDescriptionDisplay(ev.currentTarget.checked)} />
          } />
      </Box>
      <Box>
        <ItemField variant='outlined' 
          label='アイテム説明'
          multiline rows={6}
          value={item.description.content}
          onChange={(ev) => changeDescription(ev.target.value)}
        />
      </Box>
    </Box>
  );
}

//テキスト型図形　詳細タブ
type TextShapeDetailProps = {
  id?: string;
  shape: CardTemplateTextShape | CardTextShape;
  changeItem: any;
  changeBackgroundDisplay: (display: boolean) => void;
  changeBackgroundColor: (color: string) => void;
  font: FontType;
}
const TextShapeDetail: React.FC<TextShapeDetailProps> = ({id, shape, changeItem, changeBackgroundDisplay, changeBackgroundColor, font}) => {
  const changeText = (value: string) => {
    const newShape = deepCopy(shape);
    newShape.text = value;
    changeItem(newShape);
  }
  const fontUpdate = (newFont: FontType) => {
    const newShape = deepCopy(shape);
    newShape.font = newFont;
    changeItem(newShape);
  }

  return (
    <Box mt={2}>
      <ItemNameInput item={shape} changeItem={changeItem} />
      <Box mt={6}>
        <ItemField variant='outlined' 
          label='テキスト' value={shape.text}
          onChange={(ev) => changeText(ev.currentTarget.value)}
        />
      </Box>
      <Box mt={3}>
        <EditItemFrame id={id} item={shape} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <Grid container alignItems="center">
          <FormControlLabel label="背景表示"
            control={
              <Checkbox checked={shape.background.display} color="primary"
                onChange={(ev) => changeBackgroundDisplay(ev.currentTarget.checked)} />
            } />
          <ColorPalette
            currentValue={shape.background.color}
            onChange={changeBackgroundColor}
            disabled={!shape.background.display}
          />
        </Grid>
      </Box>
      <Box mt={3}>
        <EditItemFont kind='text' font={font} onUpdate={fontUpdate} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={shape} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

//四角型図形　詳細タブ
type RectangleShapeDetailProps = {
  shape: CardTemplateRectangleShape | CardRectangleShape;
  changeItem: any;
}
const RectangleShapeDetail: React.FC<RectangleShapeDetailProps> = ({shape, changeItem}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={shape} changeItem={changeItem} />
      <Box mt={3}>
        <EditShape shape={shape} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={shape} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

//円型図形　詳細タブ
type CircleShapeDetailProps = {
  shape: CardTemplateCircleShape | CardCircleShape;
  changeItem: any;
}
const CircleShapeDetail: React.FC<CircleShapeDetailProps> = ({shape, changeItem}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={shape} changeItem={changeItem} />
      <Box mt={3}>
        <EditShape shape={shape} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={shape} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

//ふきだし型図形　詳細タブ
type BalloonShapeDetailProps = {
  shape: CardTemplateBalloonShape | CardBalloonShape;
  changeItem: any;
}
const BalloonShapeDetail: React.FC<BalloonShapeDetailProps> = ({shape, changeItem}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={shape} changeItem={changeItem} />
      <Box mt={3}>
        <EditShape shape={shape} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <EditItemPosition item={shape} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

type ImageShapeDetailProps = {
  id?: string;
  shape: CardTemplateImageShape | CardImageShape;
  changeItem: any;
  changeBackgroundDisplay: (display: boolean) => void;
  changeBackgroundColor: (color: string) => void;
  onTrimming: () => void;
}
const ImageShapeDetail: React.FC<ImageShapeDetailProps> = ({id, shape, changeItem, changeBackgroundDisplay, changeBackgroundColor, onTrimming}) => {
  return (
    <Box mt={2}>
      <ItemNameInput item={shape} changeItem={changeItem} />
      <Box mt={2}>
        <Button variant="outlined" color="primary" onClick={onTrimming}>トリミングする</Button>
      </Box>
      <Box mt={3}>
        <EditItemFrame id={id} item={shape} changeItem={changeItem} />
      </Box>
      <Box mt={3}>
        <Grid container alignItems="center">
          <FormControlLabel label="背景表示"
            control={
              <Checkbox checked={shape.background.display} color="primary"
                onChange={(ev) => changeBackgroundDisplay(ev.currentTarget.checked)} />
            } />
          <ColorPalette
            currentValue={shape.background.color}
            onChange={changeBackgroundColor}
            disabled={!shape.background.display}
          />
        </Grid>
      </Box>
      <Box mt={3}>
        <EditItemPosition item={shape} changeItem={changeItem} />
      </Box>
    </Box>
  );
}

//デザインタブ-詳細タブ
type ItemSetAreaType = {
  id?: string;
  item: CardTemplateAttributeDb | CardTemplateShapeDb | CardAttributeDb | CardShapeDb | null;
  changeItem: any;
  deleteItem?: () => void;
}
const ItemSetArea: React.FC<ItemSetAreaType> = ({id, item, changeItem, deleteItem}) => {
  type TrimmingActionType = { show: boolean; }
  const [trimmingAction, setTrimmingAction] = useState<TrimmingActionType>({ show: false });

  const changeBackgroundDisplay = (newValue: boolean) => {
    if (!item) return;
    const newItem = deepCopy(item);
    newItem.background.display = newValue;
    changeItem(newItem);
  }
  const changeBackgroundColor = (newValue: string) => {
    if (!item) return;
    const newItem = deepCopy(item);
    newItem.background.color = newValue;
    changeItem(newItem);
  }
  const changeTrimData = (data: ImageData) => {
    if (isAttribute(item) && isImage(item)) {
      const newItem = deepCopy(item);
      newItem.defaultImage = data;
      changeItem(newItem);
    } else if (isShape(item) && isImage(item)) {
      const newItem = deepCopy(item);
      newItem.image = data;
      changeItem(newItem);
    }
  }
  const changeDescriptionDisplay = (display: boolean) => {
    if (!isAttribute(item)) return;
    const newItem = deepCopy(item);
    newItem.description.display = display;
    changeItem(newItem);
  }

  return (
    <>
      {(isAttribute(item) && (item.cardAttributeModel === 'single' || item.cardAttributeModel === 'multi')) && 
        <TextAttributeDetail id={id} item={item} changeItem={changeItem}
          changeBackgroundDisplay={changeBackgroundDisplay} changeBackgroundColor={changeBackgroundColor}
          font={item.font} changeDescriptionDisplay={changeDescriptionDisplay}
        />
      }
      {(isAttribute(item) && item.cardAttributeModel === 'table' && typeof id === 'string') &&
        <TableAttributeDetail id={id} item={item} changeItem={changeItem}
          font={item.font} changeDescriptionDisplay={changeDescriptionDisplay}
        />
      }
      {(isAttribute(item) && item.cardAttributeModel === 'image') &&
        <>
          <ImageAttributeDetail item={item} changeItem={changeItem}
            changeBackgroundDisplay={changeBackgroundDisplay} changeBackgroundColor={changeBackgroundColor}
            onTrimming={() => setTrimmingAction({ show: true })}
            changeDescriptionDisplay={changeDescriptionDisplay}
          />
          <TrimmingDialog open={trimmingAction.show} onClose={() => setTrimmingAction({ show: false })}
            data={item.defaultImage} changeData={changeTrimData}
          />
        </>
      }
      {(isAttribute(item) && item.cardAttributeModel === 'nondisplay') && 
        <NondisplayAttributeDetail item={item} changeItem={changeItem}
          changeDescriptionDisplay={changeDescriptionDisplay}
        />
      }
      {(isShape(item) && item.kind === 'text') &&
        <TextShapeDetail id={id} shape={item} changeItem={changeItem}
          changeBackgroundDisplay={changeBackgroundDisplay} changeBackgroundColor={changeBackgroundColor}
          font={item.font}
        />
      }
      {(isShape(item) && item.kind === 'rectangle') &&
        <RectangleShapeDetail shape={item} changeItem={changeItem} />
      }
      {(isShape(item) && item.kind === 'circle') &&
        <CircleShapeDetail shape={item} changeItem={changeItem} />
      }
      {(isShape(item) && item.kind === 'balloon') &&
        <BalloonShapeDetail shape={item} changeItem={changeItem} />
      }
      {(isShape(item) && item.kind === 'image') &&
        <>
          <ImageShapeDetail id={id} shape={item} changeItem={changeItem}
            changeBackgroundDisplay={changeBackgroundDisplay} changeBackgroundColor={changeBackgroundColor}
            onTrimming={() => setTrimmingAction({ show: true })}
          />
          <TrimmingDialog open={trimmingAction.show} onClose={() => setTrimmingAction({ show: false })}
            data={item.image} changeData={changeTrimData} />
        </>
      }
      { (isTemplate(item) || isShape(item)) &&
        <Box mt={2}>
          <Button color='primary' variant='text' onClick={deleteItem}>この項目を削除する</Button>
        </Box>
      }
    </>
  );
}

export default ItemSetArea;
